import { computed, inject, Injectable, signal } from '@angular/core'
import { Playlist } from '@models/playlist.model'
import { CreatePlaylistRequest, PlaylistApiService } from './playlist-api.service'
import { ToasterService } from './toaster.service'
import { map, Observable } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class PlaylistHelperService {
  private playlists = signal<Playlist[]>([])
  private _newlyAddedPlaylistId = signal<string | null>(null)
  private _exportingPremiereIds = signal<ReadonlySet<string>>(new Set<string>())

  private playlistApiService = inject(PlaylistApiService)
  private toasterService = inject(ToasterService)

  getPlaylists = computed(() => this.playlists())
  newlyAddedPlaylistId = this._newlyAddedPlaylistId.asReadonly()
  exportingPremiereIds = this._exportingPremiereIds.asReadonly()

  clearNewlyAddedPlaylistId(): void {
    this._newlyAddedPlaylistId.set(null)
  }

  constructor() {
    this.loadPlaylists()
  }

  loadPlaylists(): void {
    this.playlistApiService.getAllPlaylists$().subscribe(response => {
      this.playlists.set(response.playlists)
      this.savePlaylists()
    })
  }

  savePlaylists(): void {
    localStorage.setItem('media-hub-playlists', JSON.stringify(this.playlists()))
  }

  getPlaylist(playlistId: string): Playlist | undefined {
    return this.playlists().find(p => p.id === playlistId)
  }

  createPlaylist(
    name: string = 'Untitled',
    options?: {
      mediaAssetChatId?: string
      generalChatId?: string
      mediaAssetIds?: string[]
      mediaLibraryAssetIds?: string[]
    }
  ): Observable<Playlist> {
    const createPlaylistRequest: CreatePlaylistRequest = {
      name: name,
      description: '',
      mediaAssetIds: options?.mediaAssetIds || [],
      mediaLibraryAssetIds: options?.mediaLibraryAssetIds || [],
      tags: [],
      thumbnailUrl: '',
      mediaAssetChatId: options?.mediaAssetChatId,
      generalChatId: options?.generalChatId,
    }

    return this.playlistApiService.createPlaylist$(createPlaylistRequest).pipe(
      map(response => {
        if (response.success) {
          this.playlists.update(playlist => [...playlist, response.playlist])
          this.toasterService.success(response.message!)
        }
        return response.playlist
      })
    )
  }

  addAssetToPlaylist(playlistId: string, assetIds: string[]): void {
    const playlist = this.getPlaylist(playlistId)
    if (!playlist) return

    const existingIds = playlist.mediaAssetIds || []
    const updatedAssetIds = [...existingIds, ...assetIds.filter(id => !existingIds.includes(id))]

    this.playlistApiService.savePlaylistAssets$(playlistId, updatedAssetIds).subscribe({
      next: response => {
        if (response.success) {
          this.playlists.update(playlists =>
            playlists.map(p => {
              if (p.id === playlistId) {
                return {
                  ...p,
                  mediaAssetIds: updatedAssetIds,
                }
              }
              return p
            })
          )
          if (response.message) {
            this.toasterService.success(response.message)
          }
        }
        this.savePlaylists()
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to add asset to playlist')
      },
    })
  }

  removeAssetFromPlaylist(playlistId: string, assetIds: string[]): void {
    this.playlistApiService.savePlaylistAssets$(playlistId, assetIds).subscribe({
      next: response => {
        if (response.success) {
          this.playlists.update(playlists =>
            playlists.map(p => {
              if (p.id === playlistId) {
                return {
                  ...p,
                  mediaAssetIds: assetIds,
                  updatedAt: new Date().toISOString(),
                }
              }
              return p
            })
          )
          if (response.message) {
            this.toasterService.success(response.message)
          }
          this.savePlaylists()
        }
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to remove asset from playlist')
      },
    })
  }

  reorderAssets(playlistId: string, assetIds: string[]): void {
    this.playlistApiService.savePlaylistAssets$(playlistId, assetIds).subscribe({
      next: response => {
        if (response.success) {
          this.playlists.update(playlists =>
            playlists.map(p => {
              if (p.id === playlistId) {
                return {
                  ...p,
                  mediaAssetIds: assetIds,
                }
              }
              return p
            })
          )
          if (response.message) {
            this.toasterService.success(response.message)
          }
          this.savePlaylists()
        }
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to reorder playlist assets')
      },
    })
  }

  reorderAssetsOptimistic(playlistId: string, assetIds: string[]): void {
    this.playlists.update(playlists =>
      playlists.map(p => {
        if (p.id === playlistId) {
          return {
            ...p,
            mediaAssetIds: assetIds,
          }
        }
        return p
      })
    )

    this.playlistApiService.savePlaylistAssets$(playlistId, assetIds).subscribe({
      next: response => {
        if (response.success) {
          if (response.message) {
            this.toasterService.success(response.message)
          }
          this.savePlaylists()
        }
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to reorder playlist assets')
        this.loadPlaylists()
      },
    })
  }

  deletePlaylist(playlistId: string): void {
    this.playlistApiService.deletePlaylist$(playlistId).subscribe({
      next: response => {
        if (response.success) {
          this.playlists.update(playlists => playlists.filter(p => p.id !== playlistId))
          if (response.message) {
            this.toasterService.success(response.message)
          }
          this.savePlaylists()
        }
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to delete playlist')
      }
    })
  }

  renamePlaylist(playlistId: string, newName: string): void {
    const updatePlaylistRequest = {
      name: newName,
    }
    this.playlistApiService.updatePlaylist$(playlistId, updatePlaylistRequest).subscribe({
      next: response => {
        if (response.success) {
          this.playlists.update(playlists =>
            playlists.map(p => {
              if (p.id === playlistId) {
                return response.playlist ? response.playlist : { ...p, name: newName }
              }
              return p
            })
          )
          if (response.message) {
            this.toasterService.success(response.message)
          } else {
            this.toasterService.success('Playlist renamed successfully')
          }
        }
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to rename playlist')
      }
    })
  }

  addMediaLibraryAssetToPlaylist(playlistId: string, mediaLibraryAssetIds: string[]): void {
    this.playlistApiService.addMediaLibraryAssets$(playlistId, mediaLibraryAssetIds).subscribe({
      next: response => {
        if (response.success) {
          if (response.message) {
            this.toasterService.success(response.message)
          }
          this.loadPlaylists()
        }
      },
      error: error => {
        this.toasterService.error(error?.error?.message || 'Failed to add media library asset to playlist')
      },
    })
  }

  refreshPlaylist(playlistId: string): void {
    this.playlistApiService.getPlaylistById$(playlistId).subscribe({
      next: response => {
        if (response.playlist) {
          const exists = this.playlists().some(p => p.id === playlistId)
          if (exists) {
            this.playlists.update(playlists =>
              playlists.map(p => (p.id === playlistId ? response.playlist : p))
            )
          } else {
            this.playlists.update(playlists => [...playlists, response.playlist])
            this._newlyAddedPlaylistId.set(playlistId)
          }
          this.savePlaylists()
        }
      },
    })
  }

  isExportingPremiere(playlistId: string): boolean {
    return this._exportingPremiereIds().has(playlistId)
  }

  async exportPlaylistToPremiere(playlistId: string, playlistName?: string): Promise<void> {
    if (!playlistId || this._exportingPremiereIds().has(playlistId)) return

    this.markPremiereExport(playlistId, true)

    try {
      const name = playlistName ?? this.getPlaylist(playlistId)?.name ?? 'reel'
      const response = await this.playlistApiService.exportPlaylistAsPremiereXml(playlistId)
      const blob = response.body

      if (!blob || blob.size === 0) {
        this.toasterService.error('Premiere export returned an empty file')
        return
      }

      // CORS does not expose Content-Disposition here (the backend sets no
      // exposedHeaders), so the header read is opportunistic and the locally
      // derived name is the normal path.
      const headerFileName = this.parseContentDispositionFileName(response.headers.get('Content-Disposition'))
      this.triggerBlobDownload(blob, headerFileName ?? this.buildPremiereFileName(name))

      this.toasterService.success('Reel exported for Adobe Premiere Pro')
    } catch (error) {
      this.toasterService.error(await this.resolvePremiereExportError(error))
    } finally {
      this.markPremiereExport(playlistId, false)
    }
  }

  // Tracked as a set so one export finishing never releases another reel's in-flight marker.
  private markPremiereExport(playlistId: string, exporting: boolean): void {
    this._exportingPremiereIds.update(ids => {
      const next = new Set(ids)
      if (exporting) {
        next.add(playlistId)
      } else {
        next.delete(playlistId)
      }
      return next
    })
  }

  // Mirrors the backend's sanitization exactly: replace first, then trim.
  private buildPremiereFileName(playlistName: string): string {
    const sanitized = playlistName.replace(/[^a-zA-Z0-9_\- ]/g, '_').trim() || 'reel'
    return `${sanitized}.xml`
  }

  private parseContentDispositionFileName(header: string | null): string | null {
    if (!header) return null

    const encodedMatch = /filename\*=UTF-8''([^;]+)/i.exec(header)
    if (encodedMatch && encodedMatch[1]) {
      const raw = encodedMatch[1].trim().replace(/^"|"$/g, '')
      try {
        return decodeURIComponent(raw) || null
      } catch {
        return raw || null
      }
    }

    const plainMatch = /filename="?([^";]+)"?/i.exec(header)
    if (plainMatch && plainMatch[1]) {
      return plainMatch[1].trim() || null
    }

    return null
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    // Revoke on the next tick so Firefox/Safari can start the download first.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  // Error bodies arrive as a Blob because the request used responseType 'blob'.
  private async resolvePremiereExportError(error: unknown): Promise<string> {
    const fallback = 'Failed to export reel for Premiere'
    const body = (error as { error?: unknown } | null)?.error

    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text())
        return parsed?.message || fallback
      } catch {
        return fallback
      }
    }

    return (body as { message?: string } | null)?.message || fallback
  }
}
